import { api } from '@/shared/api';
import { unwrap, unwrapCollection } from '@/shared/api/envelope';

import {
    dashboardResourceSchema,
    dashboardSignalResourceSchema,
    occupancyEnvelopeSchema,
    onboardingResourceSchema,
    type DashboardResource,
    type DashboardSignalResource,
    type OccupancyResource,
    type OnboardingResource,
} from './types';

/**
 * Het dashboard van het bedrijf. Het zijn opgetelde keuringen, dus het staat
 * in deze entity.
 *
 * Elke waarde draagt een vervolgactie of een wachttijd (principe 7). Een getal
 * dat de API niet kan leveren is `null`: dat toont het scherm als "onbekend",
 * nooit als 0.
 */
export interface AwaitingCompletion {
    orderUuid: string;
    orderNumber: string;
    reportUuid: string | null;
    /** "Ravelijn 12, Goes" */
    address: string;
    /** "Energielabel · S. Bakker" */
    meta: string;
    waitingHours: number;
}

export interface DashboardSummary {
    week: { number: number; from: string; to: string };
    inspectionsThisWeek: { value: number; delta: string | null };
    reportTurnaround: { hours: number | null; minutes: number | null; delta: string | null };
    openReports: { value: number; waitingOver24h: number };
    /** Alleen voor wie facturen mag zien. */
    toInvoice: { amount: string; orders: number } | null;
    awaitingCompletion: AwaitingCompletion[];
}

export function toDashboardSummary(resource: DashboardResource): DashboardSummary {
    return {
        week: resource.week,
        inspectionsThisWeek: {
            value: resource.inspections_this_week.value,
            delta: resource.inspections_this_week.delta,
        },
        reportTurnaround: {
            hours: resource.report_turnaround.hours,
            minutes: resource.report_turnaround.minutes,
            delta: resource.report_turnaround.delta,
        },
        openReports: {
            value: resource.open_reports.value,
            waitingOver24h: resource.open_reports.waiting_over_24h,
        },
        toInvoice: resource.to_invoice ?? null,
        awaitingCompletion: resource.awaiting_completion.map((row) => ({
            orderUuid: row.order_uuid,
            orderNumber: row.order_number,
            reportUuid: row.report_uuid,
            address: row.address,
            meta: row.meta,
            waitingHours: row.waiting_hours,
        })),
    };
}

/**
 * Waar een signaal naartoe wijst. `plan` en `buy` zijn de twee blokken op
 * Facturatie waar keuringen bij komen: een abonnement kiezen of wisselen
 * (`#abonnement`) en keuringen kopen (`#keuringen-kopen`).
 */
export type SignalTarget = 'planning' | 'report' | 'billing' | 'plan' | 'buy' | 'inspections';

export interface DashboardSignal {
    key: string;
    kind: string;
    priority: 'high' | 'normal' | 'low';
    body: string;
    actionLabel: string;
    target: SignalTarget;
    orderUuid: string | null;
    reportUuid: string | null;
    suggestionId: string | null;
}

const TARGETS: readonly SignalTarget[] = [
    'planning',
    'report',
    'billing',
    'plan',
    'buy',
    'inspections',
];

export function toDashboardSignal(resource: DashboardSignalResource): DashboardSignal {
    return {
        key: resource.key,
        kind: resource.kind,
        priority:
            resource.priority === 'high' || resource.priority === 'low'
                ? resource.priority
                : 'normal',
        body: resource.body,
        actionLabel: resource.action_label,
        target: (TARGETS as readonly string[]).includes(resource.target)
            ? (resource.target as SignalTarget)
            : 'inspections',
        orderUuid: resource.order_uuid,
        reportUuid: resource.report_uuid,
        suggestionId: resource.suggestion_id,
    };
}

export interface InspectorOccupancy {
    userUuid: string;
    name: string;
    /** 0-100 en soms meer (overboekt); `null` zonder beschikbaarheid. */
    percentage: number | null;
    appointments: number;
}

export function toOccupancy(resource: OccupancyResource): InspectorOccupancy {
    return {
        userUuid: resource.user_uuid,
        name: resource.name,
        percentage: resource.percentage,
        appointments: resource.appointments,
    };
}

/** De drie eerste stappen, in de volgorde van het dashboard. */
export const ONBOARDING_STEPS = ['company_details', 'house_style', 'first_inspection'] as const;

export type OnboardingStep = (typeof ONBOARDING_STEPS)[number];

/**
 * De eerste stappen van een nieuw bedrijf. `completed` zegt of het lijstje weg
 * mag: alles klaar, of door de eigenaar verborgen. Een stap die de app niet kent,
 * valt weg.
 */
export interface Onboarding {
    completed: boolean;
    steps: { key: OnboardingStep; done: boolean }[];
}

export function toOnboarding(resource: OnboardingResource): Onboarding {
    return {
        completed: resource.completed,
        steps: resource.steps.flatMap((step) =>
            (ONBOARDING_STEPS as readonly string[]).includes(step.key)
                ? [{ key: step.key as OnboardingStep, done: step.done }]
                : [],
        ),
    };
}

export const dashboardApi = {
    /** `GET /dashboard` */
    async summary(): Promise<DashboardSummary> {
        return toDashboardSummary(unwrap(dashboardResourceSchema, await api.get('/dashboard')));
    },

    /** `GET /dashboard/signals` - belangrijkste eerst. Nooit uit de offline cache. */
    async signals(): Promise<DashboardSignal[]> {
        return unwrapCollection(
            dashboardSignalResourceSchema,
            await api.get('/dashboard/signals', { cacheKey: false }),
        ).items.map(toDashboardSignal);
    },

    /** `GET /dashboard/occupancy` - bezetting per inspecteur deze week. */
    async occupancy(): Promise<InspectorOccupancy[]> {
        return occupancyEnvelopeSchema
            .parse(await api.get('/dashboard/occupancy'))
            .data.map(toOccupancy);
    },

    /** `GET /dashboard/onboarding` - de eerste stappen; alleen de eigenaar. Nooit uit de offline cache. */
    async onboarding(): Promise<Onboarding> {
        return toOnboarding(
            unwrap(
                onboardingResourceSchema,
                await api.get('/dashboard/onboarding', { cacheKey: false }),
            ),
        );
    },

    /** `POST /dashboard/onboarding/dismiss` - het lijstje voorgoed verbergen. */
    async dismissOnboarding(): Promise<Onboarding> {
        return toOnboarding(
            unwrap(
                onboardingResourceSchema,
                await api.post('/dashboard/onboarding/dismiss', undefined, { cacheKey: false }),
            ),
        );
    },
};
